import { BoundingBox, CategorizedData, DisaggregatedMetric, GeoDetails, GeoTag, LayerItem, Metric, MetricMetadata } from "../services/types";

// Icons
// People
import PopulationIcon from '../assets/credc/Population.svg?react';
import LimitedEnglishIcon from '../assets/credc/LimitedEnglish.svg?react';
import RaceEthnicityIcon from '../assets/credc/RaceAndEthnicity.svg?react';
import DisabilityIcon from '../assets/credc/IndividualsWithDisabilities.svg?react';
import AgeIcon from '../assets/credc/PopulationByAge.svg?react';
import LaborForceParticipationRateIcon from '../assets/credc/LaborForceParticipation.svg?react';
// Economy
import MedianHouseholdIncomeIcon from '../assets/credc/MedianHouseholdIncome.svg?react';
import UnemploymentRateIcon from '../assets/credc/UnemploymentRate.svg?react';
import AverageWeeklyWageIcon from '../assets/credc/AverageWeeklyWage.svg?react';
import PovertyRateIcon from '../assets/credc/PovertyRate.svg?react';
import QualityJobsIcon from '../assets/credc/QualityJobsIndex.svg?react';
import JobsToHousingRatioIcon from '../assets/credc/JobsToHousingRatio.svg?react';
import EmploymentCountIcon from '../assets/credc/EmploymentCount.svg?react';

// Access
import InternetAccessIcon from '../assets/credc/InternetAccess.svg?react';
import HealthyFoodIcon from '../assets/credc/HealthyFoodAccess.svg?react';
import HealthInsuranceIcon from '../assets/credc/HealthInsuranceRate.svg?react';
import InternetSpeedIcon from '../assets/credc/InternetSpeed.svg?react';

// Livability
import ShareIncomeIcon from '../assets/credc/ShareIncome.svg?react';
import HomeOwnershipIcon from '../assets/credc/HomeOwnershipRate.svg?react';
import CostBurdenedIcon from '../assets/credc/CostBurdenedHouseholds.svg?react';

// Education
import HSGraduationRateIcon from '../assets/credc/HighSchoolGraduationRate.svg?react';
import { indicatorsEnv, layersEnv, metricsEnv } from "./environment";

// Temp store for CREDC Categories
export const orgCategories = ['People', 'Economy', 'Access', 'Livability', 'Education'];

// #region Organization Metric Mapping
const organizationMetrics: { [orgId: number]: any[] } =
{ 
    // CREDC
    53:
    [
        {
            name: "People",
            ids: [1205, 1226, 1228, 1229, 1230, 1231],
            order: [
                [1205, 1231, 1226, 1229]
            ],
            charts: [
                {
                    name: "raceethnicity",
                    id: 1228
                },
                {
                    name: "age",
                    id: 1230
                }
            ]
        },
        {
            name: "Livability",
            ids: [1209, 1210, 1211, 1212, 1213, 1214],
            order: [
                [1214],
            ],
            charts: []
        },
        {
            name: "Access",
            ids: [1207, 1208, 1217, 1225],
            order: [
                [1207, 1208, 1217, 1225],
            ],
            charts: []
        },
        {
            name: "Economy",
            ids: [1206, 1216, 1219, 1221, 1222, 1223, 1227],
            order: [
                [1206, 1216, 1219, 1221, 1223, 1227],
            ],
            charts: [
                {
                    name: "employment",
                    id: 1222
                },
            ]
        },
        {
            name: "Education",
            ids: [1215, 1218, 1220, 1224],
            order: [
                [1224],
            ],
            charts: []
        }
    ]
};

// precisionMapping - maps metricId's to the desired number of significant figures for formatting metric values
export const precisionMapping: { [metricId: number]: number } = {
    1207: 0,
    1208: 0,
    1217: 0,
    1226: 0,
    1209: 0,
    1210: 0,
    1211: 0,
    1212: 0,
    1213: 0,
    1214: 0,
    1215: 0,
    1218: 0,
    1220: 0,
    1221: 1,
    1223: 1,
    1224: 0,
    1225: 0,
    1229: 0,
    1231: 0,
}; 


const svgMap: { [orgId: number]: { [key: number]: React.FC<React.SVGProps<SVGSVGElement>> }} =
{
    // CREDC
    53: {
        1205: PopulationIcon,
        1226: LimitedEnglishIcon,
        1228: RaceEthnicityIcon,
        1229: DisabilityIcon,
        1230: AgeIcon,
        1231: LaborForceParticipationRateIcon,
        1206: JobsToHousingRatioIcon,
        1216: MedianHouseholdIncomeIcon,
        1219: AverageWeeklyWageIcon,
        1221: PovertyRateIcon,
        1222: EmploymentCountIcon,
        1223: UnemploymentRateIcon,
        1227: QualityJobsIcon,
        1209: ShareIncomeIcon,
        1210: ShareIncomeIcon,
        1211: CostBurdenedIcon,
        1214: HomeOwnershipIcon,
        1207: InternetAccessIcon,
        1208: HealthyFoodIcon,
        1217: HealthInsuranceIcon,
        1225: InternetSpeedIcon,
        1224: HSGraduationRateIcon,
    },
};

// There are svgs that need to be included in the pdf that are excluded in the dashboard
const pdfSvgMap: { [orgId: number]: { [key: number]: React.FC<React.SVGProps<SVGSVGElement>> }} =
{
    // CREDC
    53: {
        1205: PopulationIcon,
        1226: LimitedEnglishIcon,
        1228: RaceEthnicityIcon,
        1229: DisabilityIcon,
        1230: AgeIcon,
        1231: LaborForceParticipationRateIcon,
        1206: JobsToHousingRatioIcon,
        1216: MedianHouseholdIncomeIcon,
        1219: AverageWeeklyWageIcon,
        1221: PovertyRateIcon,
        1222: EmploymentCountIcon,
        1223: UnemploymentRateIcon,
        1227: QualityJobsIcon,
        1209: ShareIncomeIcon,
        1210: ShareIncomeIcon,
        1211: CostBurdenedIcon,
        1214: HomeOwnershipIcon,
        1207: InternetAccessIcon,
        1208: HealthyFoodIcon,
        1217: HealthInsuranceIcon,
        1225: InternetSpeedIcon,
        1224: HSGraduationRateIcon,
    },
};

// #endregion



// getMetricGroupIds - get the metric id values for a specific grouping 
export const getMetricGroupIds = (name: string) => {

    let ids = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            ids = group.ids;
        }
    }

    return ids;
}

// Store our organization Id where we can access it later
export let orgId: number = 53;
export const setOrgId = (id: number) => {
    orgId = id;
}

// Store our overlapping county fips codes for BLS and TRS data queries
export var countyId: string[] = [];
export const setCountyIds = (id: string[]) => {
    countyId = id;
}
export const getCountyIds = () => {
    return countyId;
}

// Store our bounding box so we can access it later
export let boundingBox: BoundingBox | null = null;
export const setBoundingBox = (bb: BoundingBox) => {
    boundingBox = bb;
}

// Store our excludeIds so we can access them later
export let excludeIds: string[] | null = null;
export const setExcludeIds = (ids: string[]) => {
    excludeIds = ids;
}

// getSvgMap - get our svg mapping for the given organization and metric id
export const getSvgMap = (id: number): React.FC<React.SVGProps<SVGSVGElement>> | undefined => {
    if(svgMap[orgId] != undefined){
        return svgMap[orgId][id];
    }
    return undefined;
}

// getPdfSvgMap - get our pdf svg mapping for the given organization and metric id
export const getPdfSvgMap = (id: number): React.FC<React.SVGProps<SVGSVGElement>> | undefined => {
    if(pdfSvgMap[orgId] != undefined){
        return pdfSvgMap[orgId][id];
    }
    return undefined;
}



// getMetricGroupOrderIds - get the ordering for a metric group
export const getMetricGroupOrderIds = (name: string) => {
    let orderIds = [];
    
    if(organizationMetrics[orgId] != undefined){
        const group: any = organizationMetrics[orgId].find((x: { name: string; }) => x.name === name);

        if(group != undefined){
            orderIds = group.order;
        }
    }

    return orderIds;
}

// getMetricGroupChartId - get a metric id given the group and chart key
export const getMetricGroupChartId = ( group: string, key: string) => {
    let id = -1;

    if(organizationMetrics[orgId] != undefined){
        const g = organizationMetrics[orgId].find((x: { name: string; }) => x.name === group);
        if(g != undefined){
            
            if(g.charts != undefined){

                const chart = g.charts.find((x: { name: string; }) => x.name === key);
                if(chart != undefined){
                    id = chart.id;
                }
            }
        }
    }

    return id;
}

export const categorizeData = (indicators: DisaggregatedMetric[]) => {

    const categories: { [key: string]: number[] } = {
        People: getMetricGroupIds("People"),
        Livability: getMetricGroupIds("Livability"),
        Economy: getMetricGroupIds("Economy"), 
        Access: getMetricGroupIds("Access"),
        Education: getMetricGroupIds("Education"),
    };

    // Initialize the categorizedData object dynamically with empty arrays for each category
    const categorizedData: Record<string, Metric[]> = Object.keys(categories).reduce((acc, category) => {
        acc[category] = [];
        return acc;
    }, {} as Record<string, Metric[]>);

    // Categorize the indicators dynamically
    indicators.forEach((item) => {
        Object.keys(categories).forEach((category) => {
            if (categories[category].includes(item.id)) {
                categorizedData[category].push(item);
            }
        });
    });

    return categorizedData;
};

export const fetchMetricCategories = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetriccategories?orgId=${orgId}&environment=${metricsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadata = async (id: number) => {
    if (!id) {
        console.log('No id provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetricmetadata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id, environment: metricsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

export const fetchMetricMetadataByOrg = async (orgId: number) => {
    if (!orgId) {
        console.log('No orgId provided to fetch metric metadata');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetadatabyorg`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orgId, environment: metricsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.log('Error fetching metric metadata:', error);
    }
};

// fetch PercentileDist - get percentile distribution information by org and category
export const fetchPercentileDist = async (orgId: number, category: string) => {
    if (!orgId || !category) {
        console.log('No orgId or category provided to fetch metric percentile distributions');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentiledist?orgId=${orgId}&category=${category}&environment=${indicatorsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric metadata:', error);
    }
};

// fetchPercentileById - get percentile distribution information by metric id
export const fetchPercentileById = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch metric percentile data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getpercentilebymetric?metricId=${metricId}&environment=${indicatorsEnv}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching percentile data:', error);
    }
};

// fetchDistributionData - get distribution data by metric id and selected area
export const fetchDistributionData = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getdistributiondata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchIndicatorBinCount - get indicator data and bin count by metric id and selected area
export const fetchIndicatorBinCount = async (metricId: number, geoDetails: GeoDetails[], geoTag: GeoTag | null) => {
    if (!metricId) {
        console.log('No metricId provided to fetch indicator distribution data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getindicatorbincounts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoDetails, geoTag, geoEncoding: 'delta', allowDefault: true, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching indicator distribution data:', error);
    }
};

// fetchStateData - retreive state data for a selected metric using a prefix state code
export const fetchStateData = async (metricId: number, geoPrefix: string) => {
    if (!metricId || !geoPrefix) {
        console.log('No metricId or geoDetails provided to fetch metric state data');
        return;
    }

    console.log('geoprefix:', geoPrefix);

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, geoPrefix, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching metric state data:', error);
    }
};

// fetchNationalData - retreive national data for a selected metric
export const fetchNationalData = async (metricId: number) => {
    if (!metricId) {
        console.log('No metricId provided to fetch national metric data');
        return;
    }

    try {
        const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getgeoprefixdata`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ metricId, environment: indicatorsEnv }),
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        const result = await response.json();
        return result;
    } catch (error: any) {
        console.error('Error fetching national metric data:', error);
    }
};

export const calculateGeoPrefix = (geoDetails: GeoDetails[]): string => {
    // Flatten the geoIds array
    const geoIds = geoDetails.flatMap(geo => geo.ids);

    // Helper function to get the first two digits of a geoId
    const getGeoPrefix = (geoId: string): string => geoId.substring(0, 2);

    // Count occurrences of each prefix
    const prefixCount: Record<string, number> = {};
    geoIds.forEach(geoId => {
        const prefix = getGeoPrefix(geoId);
        prefixCount[prefix] = (prefixCount[prefix] || 0) + 1;
    });

    // Find the prefix with the highest count
    return Object.entries(prefixCount).reduce((prev, curr) => 
        curr[1] > prev[1] ? curr : prev, ['', 0]
    )[0];
};

// getMissingMetrics - utility function to check for missing metrics on a given dashboard panel
export const getMissingMetrics = (
    tab: string,
    categorizedData: Metric[],
    indicators: DisaggregatedMetric[] | null
  ): string => {
    if (!indicators) return '';
  
    const missingMetrics = indicators?.filter(metric => 
        metric.category?.toLowerCase() === tab.toLowerCase() && 
        !categorizedData.some(data => data.id === metric.id)
    ) || [];    
  
    return missingMetrics.map(metric => metric.name).join(", ");
};

// Helper function to get and sort metrics by category
export const getSortedMetrics = (category: string, categorizedData: CategorizedData) => {
    const orders = getMetricGroupOrderIds(category);

    return orders.map((order: number[]) => 
        categorizedData[category]
            .filter(metric => order.includes(metric.id))
            .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};

// Helper function to get and sort metrics by category - Used in PDF metrics display
export const getSortedMetricsPDF = (category: string, categorizedData : {
    category: string;
    metrics: DisaggregatedMetric[];
}[]): DisaggregatedMetric[] => {
    const metrics = categorizedData.find(data => data.category === category)?.metrics || [];
    const orders = getMetricGroupOrderIds(category);
    return orders.flatMap((order: number[]) =>
      metrics
        .filter(metric => order.includes(metric.id))
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
};
  
// Helper function to get the index metric data
export const getIndexMetricData = (
    category: string, 
    metadata: MetricMetadata[] | null, 
    disaggregatedData: DisaggregatedMetric[]
): { indexMetricId: number; indexMetricData: DisaggregatedMetric | null } => {
    
    const indexMetricId = metadata?.find(
        (metric) => 
            metric.metric_source_type === "index" && 
            metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;

    const indexMetricData = disaggregatedData.find(metric => metric.id === indexMetricId) || null;

    return { indexMetricId, indexMetricData };
};

// Helper function to get the index metric data - Formatted for PDF (MetricModal) use.
export const getIndexMetricDataPDF = (category: string, metadata: MetricMetadata[] | null, disaggregatedData: DisaggregatedMetric[]): DisaggregatedMetric | null => {
    const indexMetricId = metadata?.find(
        (metric) => metric.metric_source_type === "index" && metric.metric_category.toLowerCase() === category.toLowerCase()
    )?.id || 0;
    return disaggregatedData.find(metric => metric.id === Number(indexMetricId)) || null;
};

export async function getItemDetails(layerId: number, itemId: number): Promise<LayerItem | null> {
  try {
    const res = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayeritemdetails?layerId=${layerId}&itemId=${itemId}&environment=${layersEnv}`);
    if (!res.ok) {
      throw new Error(`Error ${res.status}: ${res.statusText}`);
    }

    const data = await res.json();
    console.log('item data:', data);
    return data[0] ?? null;
  } catch (err) {
    console.error('Error fetching item details:', err);
    return null;
  }
}
